import { useState } from 'react';

import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';

import { useAppSelector } from 'hooks';
import { getDeviceImageOrNone, formatDeviceSizeRange } from 'utils/Devices';

import { IInvitation } from '../Onboard';
import { MAX_BULK_QUANTITY } from './Devices';

interface IAccessoriesListProps {
  accessories: IDeviceOrderingCatalogItem[];
}

const AccessoriesList = ({ accessories }: IAccessoriesListProps) => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<IInvitation>();
  const deviceOrderingsAvailable = useAppSelector((state) => state.groups.deviceOrderingsAvailable);
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});

  const getDetails = (accessory: IDeviceOrderingCatalogItem): IDeviceOrderingDetails | null => {
    const parent = deviceOrderingsAvailable.find(
      (d) => d.deviceOrderingId === accessory.parentDeviceOrderingId
    );
    return parent?.options.find((o) => o.sku === accessory.sku)?.details ?? null;
  };

  const grouped = accessories
    .slice()
    .sort((a, b) => a.lineOrder - b.lineOrder)
    .reduce(
      (acc, accessory) => {
        const key = accessory.displayType;
        if (!acc[key]) acc[key] = [];
        acc[key].push(accessory);
        return acc;
      },
      {} as Record<string, IDeviceOrderingCatalogItem[]>
    );

  const handleToggle = (sku: string) => {
    const next = { ...values.accessories };
    if (next[sku] !== undefined) {
      delete next[sku];
    } else {
      next[sku] = 1;
    }
    setFieldValue('accessories', next);
  };

  const handleQtyChange = (sku: string, raw: number) => {
    const qty = Math.max(1, Math.min(MAX_BULK_QUANTITY, raw));
    setFieldValue('accessories', { ...values.accessories, [sku]: qty });
  };

  return (
    <>
      {Object.entries(grouped).map(([groupKey, items]) => (
        <div key={groupKey} className="mb-4">
          <p className="text-uppercase mb-3 small fw-semibold text-secondary device-group-label">
            {intl.formatMessage({ id: groupKey })}
          </p>
          <div className="d-flex flex-column gap-2">
            {items.map((accessory) => {
              const qty = values.accessories[accessory.sku] ?? 0;
              const isSelected = qty > 0;
              const imageUrl = getDeviceImageOrNone(accessory.displayName);
              const details = getDetails(accessory);
              const name = intl.formatMessage({
                id: accessory.displayName,
                defaultMessage: accessory.displayName
              });

              return (
                <div
                  key={accessory.sku}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() => handleToggle(accessory.sku)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggle(accessory.sku);
                    }
                  }}
                  className={`d-flex align-items-center p-3 rounded-3 accessory-card${isSelected ? ' accessory-card--selected' : ''}`}
                >
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt={name}
                      style={{ width: 60, height: 60, objectFit: 'contain' }}
                      className="me-3 flex-shrink-0"
                    />
                  )}
                  <div className="flex-grow-1 me-3">
                    <div className="fw-semibold">{name}</div>
                    <div className="text-secondary small">
                      {accessory.sku}
                      {details && (
                        <span className="ms-2 text-primary">{formatDeviceSizeRange(details)}</span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      className="d-flex align-items-center gap-2 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="small mb-0 text-secondary">
                        {intl.formatMessage({ id: 'qtyKey' })}:
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={MAX_BULK_QUANTITY}
                        className="form-control form-control-sm"
                        style={{ width: 70 }}
                        value={quantityDrafts[accessory.sku] ?? String(qty)}
                        onChange={(e) => {
                          setQuantityDrafts((drafts) => ({
                            ...drafts,
                            [accessory.sku]: e.target.value
                          }));
                          const parsed = parseInt(e.target.value, 10);
                          if (!Number.isNaN(parsed)) handleQtyChange(accessory.sku, parsed);
                        }}
                        onBlur={() => {
                          const parsed = parseInt(quantityDrafts[accessory.sku] ?? '', 10);
                          const clamped = Number.isNaN(parsed)
                            ? qty
                            : Math.max(1, Math.min(MAX_BULK_QUANTITY, parsed));
                          setQuantityDrafts((drafts) => {
                            const next = { ...drafts };
                            delete next[accessory.sku];
                            return next;
                          });
                          handleQtyChange(accessory.sku, clamped);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
};

export default AccessoriesList;
